/* eslint-disable max-nested-callbacks */
import React, { useState, useMemo, useRef, useReducer, useEffect } from 'react';
import { useMount, useLocalStorageState } from 'ahooks';
import cloneDeep from 'lodash.clonedeep';
import classNames from 'classnames';
import loadResource from '@/utils/loader';
import { bindEvents } from '@/utils/sensors-data';
import ProjectTabs from '@/components/home/project-tabs';
import Banner from '@/components/home/banner';
import StudentInfo from '@/components/home/student-info';
import AlertBanner from '@/components/home/alert-banner';
import Courses from '@/components/home/courses';
import LearningTools from '@/components/home/learning-tools';
import CalendarSchedule from '@/components/home/calendar-schedule';
import SensorsDataContext from '@/components/home/context';
import { getStudyTool, getProjectsAndCourses, getStudentInfo, getStudentExtraInfo, getResourcesCover, sortPackageCourse } from '@/apis';
import { IVCourseResult, IStudyToolResult, type IStudentInfo, type ICourseListItem, type IResourcesCoverListItem } from '@/types';
import './index.less';
import { environment } from '@gaodun.com/gtools';

let indexNumber = 0;
const Index = () => {
    const [localProjectId, setLocalProjectId] = useLocalStorageState<number | undefined>('gd-space-local-storage-state-project', {
        defaultValue: 0,
    });

    const [activeProjectId, setActiveProjectId] = useState(0);
    const [activeProjectName, setActiveProjectName] = useState('');
    const [activeCourseStatusKey, setActiveCourseStatusKey] = useState(0);

    const [studentInfo, setStudentInfo] = useState<IStudentInfo>({
        studentId: 0,
        nickName: '',
        headImgUrl: '',
        days: 0,
        itemDone: 0,
    });
    const [projectList, setProjectList] = useState<IVCourseResult['projectList']>([]);
    const [courseListMap, setCourseListMap] = useState<IVCourseResult['courseList']>({});
    const [expiredCourseListMap, setExpiredCourseListMap] = useState<IVCourseResult['courseList']>({});
    const [studyToolMap, setStudyToolMap] = useState<{ [projectId: number]: IStudyToolResult }>({});
    const [resourcesCover, setResourcesCover] = useState<IResourcesCoverListItem[]>([]);
    const [collapsedCoursePackageIds, setCollapsedCoursePackageIds] = useState<number[]>([]);

    const isFetchExpiredCourses = useRef(false);

    const coursesListDataSource = useMemo(() => {
        if (activeCourseStatusKey === 2) {
            return expiredCourseListMap[activeProjectId] || [];
        }
        return courseListMap[activeProjectId] || [];
    }, [activeProjectId, activeCourseStatusKey, expiredCourseListMap, courseListMap]);

    const coursesSubjects = useMemo(() => {
        if (activeCourseStatusKey === 2) {
            return [];
        }

        const subjects =
            coursesListDataSource
                .filter(item => item.subjectId)
                .map(item => ({ subjectId: item.subjectId, subjectName: item.subjectName })) || [];
        const uniqueCourses = Array.from(new Map(subjects.reverse().map(item => [item.subjectId, item])).values());
        return uniqueCourses;
    }, [coursesListDataSource]);

    const sensorsDataContextValues = useMemo(() => {
        return {
            student_id: studentInfo.studentId,
            current_projectid: activeProjectId,
            current_projectname: activeProjectName,
        };
    }, [studentInfo.studentId, activeProjectId, activeProjectName]);

    const fetchStudentInfo = async () => {
        const responses = await Promise.all([getStudentInfo(), getStudentExtraInfo()]);
        const newStudentInfo: IStudentInfo = {
            studentId: 0,
            nickName: '',
            headImgUrl: '',
            days: 0,
            itemDone: 0,
        };
        if (responses[0].status === 0 && responses[0].data) {
            newStudentInfo.nickName = responses[0].data.nickName;
            newStudentInfo.headImgUrl = responses[0].data.headImgUrl;
            newStudentInfo.days = responses[0].data.days;
            newStudentInfo.studentId = responses[0].data.userId;
            // 支持B端页面跳转到 C端，打通登录体系。
            localStorage.setItem(
                `${environment.getApiEnv()}userData`,
                JSON.stringify({
                    isLogin: true,
                    NickName: newStudentInfo.nickName,
                    PictureUrl: newStudentInfo.headImgUrl,
                    StudentId: newStudentInfo.studentId,
                })
            );
        }
        if (responses[1].status === 0 && responses[1].result) {
            newStudentInfo.itemDone = responses[1].result.item_done;
        }
        setStudentInfo(newStudentInfo);
    };

    const fetchProjectsAndCourses = async () => {
        const response = await getProjectsAndCourses();
        if (response.status === 0 && response.result) {
            return response.result;
        }
        return undefined;
    };

    const fetchExpiredCourses = async () => {
        if (isFetchExpiredCourses.current) return;

        const response = await getProjectsAndCourses({ courseStatus: 2 });
        if (response.status === 0 && response.result) {
            isFetchExpiredCourses.current = true;
            setExpiredCourseListMap(response.result.courseList);
        }
    };

    const fetchStudyTool = async (projectId: number) => {
        const response = await getStudyTool({
            projectId,
            source: 1,
        });
        if (response.status === 0 && response.result) {
            setStudyToolMap(oldValues => {
                return { ...oldValues, [projectId]: response.result };
            });
        }
    };

    const fetchResourcesCover = async (learnResourceIds: number[]) => {
        if (!learnResourceIds?.length) return;

        const response = await getResourcesCover({
            resourceIds: learnResourceIds.join(','),
        });
        if (response.status === 0 && response.result) {
            setResourcesCover(response.result);
        }
    };

    // 切换项目
    const handleOnChangeProjectTab = (newActiveProjectId: number, newActive: IVCourseResult['projectList'][number]) => {
        setLocalProjectId(newActiveProjectId);
        setActiveProjectId(newActiveProjectId);
        setActiveProjectName(newActive.name);
        setActiveCourseStatusKey(0);
        if (!studyToolMap[newActiveProjectId]) {
            fetchStudyTool(newActiveProjectId);
        }
    };

    // 切换课程tab
    const handleOnChangeCourseStatusTab = (activeKey: number, active: { key: number; name: string }) => {
        bindEvents('pc_studytab_tab_click', {
            ...sensorsDataContextValues,
            tab_name: active.name,
        });
        setActiveCourseStatusKey(activeKey);
        if (activeKey === 2) {
            fetchExpiredCourses();
        }
    };

    /* 折叠大包课 */
    const handleOnCollapsedCoursePackage = (courseId: number) => {
        setCollapsedCoursePackageIds(oldValues => {
            const newValues = [...oldValues];
            if (newValues.includes(courseId)) {
                newValues.splice(newValues.indexOf(courseId, 1));
                return newValues;
            }
            newValues.push(courseId);
            return newValues;
        });
    };

    /* 大包课下课程排序 */
    const handleOnSortCoursePackage = (courseId: number, newCoursePackage: ICourseListItem) => {
        sortPackageCourse(courseId, { courseIds: newCoursePackage.children.map(child => child.id) });
        setCourseListMap(oldValue => {
            const newValue = cloneDeep(oldValue) as IVCourseResult['courseList'];
            const updateIndex = newValue[activeProjectId].findIndex(course => course.id === courseId);
            newValue[activeProjectId].splice(updateIndex, 1, newCoursePackage);
            return newValue;
        });
    };

    /* 初始化页面数据 */
    const initializeHomepageData = async () => {
        fetchStudentInfo();

        const projectsAndCoursesData = await fetchProjectsAndCourses();

        if (!projectsAndCoursesData || !projectsAndCoursesData.projectList?.length) {
            fetchStudyTool(activeProjectId);
            return;
        }

        const localProject = projectsAndCoursesData.projectList.find(project => project.id === localProjectId);
        const newActiveProject = localProject || projectsAndCoursesData.projectList[0];

        // 当前项目的学习工具 banner数据
        fetchStudyTool(newActiveProject.id);
        // 获取封面图
        fetchResourcesCover(projectsAndCoursesData.learnResourceIds);

        setLocalProjectId(newActiveProject.id);
        setActiveProjectId(newActiveProject.id);
        setActiveProjectName(newActiveProject.name);
        setProjectList(projectsAndCoursesData.projectList);
        setCourseListMap(projectsAndCoursesData.courseList);
    };
    const hideView = () => {
        // 获取所有包含特定class的元素
        const elements = document.querySelectorAll('.top-nav-change');
        console.log('🚀 ~ hideView ~ elements:', elements, indexNumber);
        if (elements.length === 0 && indexNumber < 10) {
            setTimeout(() => {
                indexNumber = indexNumber + 1;
                hideView();
            }, 500);
        }

        // 隐藏这些元素
        elements.forEach(element => {
            if (element) {
                // @ts-ignore
                element.style.display = 'none';
            }
        });
    };

    const initializeHeader = () => {
        loadResource(`https://s.gaodun.com/web/static-share/js/jquery-1.7.1.min.js`, 'js', () => {
            loadResource(`https://s.gaodunwangxiao.com/static-resource/navigator/1.0.1/navigator3.css`, 'css', () => {
                setTimeout(() => {
                    // 获取所有包含特定class的元素
                    hideView();
                }, 0);
            });
            loadResource(`https://s.gaodunwangxiao.com/static-resource/navigator/1.0.1/navigator2.js`, 'js');
            loadResource(`https://s.gaodunwangxiao.com/static-resource/loginV2/2.2/loginV2.js`, 'js');
        });
    };

    useMount(() => {
        initializeHomepageData();

        setTimeout(() => initializeHeader(), 100);
    });

    // 跟踪课程公告曝光
    useEffect(() => {
        const courses = courseListMap[activeProjectId];
        const filterNoticeCourses = courses
            ?.flatMap(course => {
                if (course.isBig && course.children?.length) {
                    return [...course.children];
                }
                return course;
            })
            ?.filter(course => !!course.courseNotice);
        if (filterNoticeCourses?.length) {
            filterNoticeCourses.forEach(course => {
                bindEvents('pc_studytab_course_notice_view', {
                    ...sensorsDataContextValues,
                    coursenotice_name: course.courseNotice.noticeTitle,
                    course_id: course.saasCourseId,
                });
            });
        }
    }, [activeProjectId, courseListMap]);

    // 跟踪banner和公告曝光
    useEffect(() => {
        const studyToolData = studyToolMap[activeProjectId];
        if (studyToolData?.slideShows?.length) {
            studyToolData.slideShows?.forEach(slideShow => {
                bindEvents('pc_studytab_banner_view', {
                    ...sensorsDataContextValues,
                    activity_name: slideShow.bannerName,
                    activity_id: slideShow.bannerId,
                });
            });
        }
        if (studyToolData?.notice) {
            bindEvents('pc_studytab_notice_view', {
                ...sensorsDataContextValues,
                notice_name: studyToolData.notice.noticeTitle,
                notice_id: studyToolData.notice.noticeId,
            });
        }
    }, [activeProjectId, studyToolMap]);

    return (
        <SensorsDataContext.Provider value={sensorsDataContextValues}>
            <div id="topNav" />
            <div className="home-container">
                <div className="home-main">
                    <ProjectTabs dataSource={projectList} activeKey={activeProjectId} handleClick={handleOnChangeProjectTab} />
                    <div className="home-content">
                        <div className="home-content-lf">
                            {!!studyToolMap[activeProjectId]?.slideShows?.length && (
                                <Banner dataSource={studyToolMap[activeProjectId].slideShows} />
                            )}
                            {!!studyToolMap[activeProjectId]?.notice && <AlertBanner dataSource={studyToolMap[activeProjectId].notice} />}
                            <Courses
                                dataSource={coursesListDataSource}
                                resourcesCover={resourcesCover}
                                activeKey={activeCourseStatusKey}
                                handleClickTab={handleOnChangeCourseStatusTab}
                                collapsedIds={collapsedCoursePackageIds}
                                handleCollapsed={handleOnCollapsedCoursePackage}
                                handleDragEnd={handleOnSortCoursePackage}
                            />
                        </div>
                        <div
                            className={classNames('home-content-rt', {
                                'padding-top-36':
                                    !studyToolMap[activeProjectId]?.slideShows?.length && !studyToolMap[activeProjectId]?.notice,
                            })}
                        >
                            <StudentInfo dataSource={studentInfo} />
                            {!!studyToolMap[activeProjectId]?.learningTools?.length && (
                                <LearningTools dataSource={studyToolMap[activeProjectId].learningTools} />
                            )}
                            <CalendarSchedule projectName={activeProjectName} projectId={activeProjectId} subjects={coursesSubjects} />
                        </div>
                    </div>
                </div>
            </div>
        </SensorsDataContext.Provider>
    );
};

export default Index;
